export interface DashboardStats {
  usersCount: number;
  vehiclesCount: number;
  rentalsCount: number;
  totalRevenue: number;
  activeRentals: number;
  availableVehicles: number;
}

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
    borderColor: string[];
    borderWidth: number;
  }[];
}

export const VEHICLE_TYPE_LABELS: Record<string, string> = {
  car: 'Voiture',
  motorcycle: 'Moto',
  scooter: 'Trottinette',
  bike: 'Vélo',
  quad: 'Quad',
  autre: 'Autre',
};

export class InternalViewModel {
  private static instance: InternalViewModel;

  private constructor() {}

  static getInstance(): InternalViewModel {
    if (!InternalViewModel.instance) {
      InternalViewModel.instance = new InternalViewModel();
    }
    return InternalViewModel.instance;
  }

  // Récupérer le nombre d'utilisateurs
  async getUsersCount(): Promise<number> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return result.length || 0;
      } else {
        console.error('Erreur lors de la récupération du nombre d\'utilisateurs:', response.status);
        return 0;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre d\'utilisateurs:', error);
      return 0;
    }
  }

  // Récupérer le nombre de véhicules
  async getVehiclesCount(): Promise<number> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return result.length || 0;
      } else {
        console.error('Erreur lors de la récupération du nombre de véhicules:', response.status);
        return 0;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre de véhicules:', error);
      return 0;
    }
  }

  // Récupérer le nombre de locations
  async getRentalsCount(): Promise<number> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/rentals`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return result.length || 0;
      } else {
        console.error('Erreur lors de la récupération du nombre de locations:', response.status);
        return 0;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre de locations:', error);
      return 0;
    }
  }

  // Récupérer toutes les statistiques du dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/dashboard/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return {
          usersCount: result.usersCount || 0,
          vehiclesCount: result.vehiclesCount || 0,
          rentalsCount: result.rentalsCount || 0,
          totalRevenue: result.totalRevenue || 0,
          activeRentals: result.activeRentals || 0,
          availableVehicles: result.availableVehicles || 0,
        };
      } else {
        console.error('Erreur lors de la récupération des statistiques:', response.status);
        return {
          usersCount: 0,
          vehiclesCount: 0,
          rentalsCount: 0,
          totalRevenue: 0,
          activeRentals: 0,
          availableVehicles: 0,
        };
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      return {
        usersCount: 0,
        vehiclesCount: 0,
        rentalsCount: 0,
        totalRevenue: 0,
        activeRentals: 0,
        availableVehicles: 0,
      };
    }
  }

  // Récupérer les données pour les graphiques
  async getChartData(period: 'week' | 'month' | 'year' = 'month'): Promise<ChartData> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/dashboard/charts?period=${period}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      } else {
        console.error('Erreur lors de la récupération des données de graphiques:', response.status);
        return {
          labels: [],
          datasets: []
        };
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données de graphiques:', error);
      return {
        labels: [],
        datasets: []
      };
    }
  }

  // Récupérer les locations actives
  async getActiveRentals(): Promise<any[]> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/rentals/active`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return result.rentals || [];
      } else {
        console.error('Erreur lors de la récupération des locations actives:', response.status);
        return [];
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des locations actives:', error);
      return [];
    }
  }

  // Récupérer les véhicules disponibles
  async getAvailableVehicles(): Promise<any[]> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles/available`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        return result.vehicles || [];
      } else {
        console.error('Erreur lors de la récupération des véhicules disponibles:', response.status);
        return [];
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des véhicules disponibles:', error);
      return [];
    }
  }

  // Récupérer les événements récents — inscriptions et locations, séparément
  // (l'appelant choisit lequel afficher). Les deux endpoints renvoient les
  // colonnes SQL brutes (id_user/registration_date, renter_id/vehicle_type/
  // created_at, pas de rental.user ni rental.vehicle imbriqués) — il faut donc
  // reconstituer le nom du locataire via la liste des utilisateurs.
  async getRecentEvents(): Promise<{ signups: any[]; rentals: any[] }> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [rentalsResponse, usersResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}api/rentals`, { method: 'GET', headers }),
        fetch(`${process.env.REACT_APP_API_URL}api/users`, { method: 'GET', headers }),
      ]);

      const users = usersResponse.ok ? await usersResponse.json() : [];
      const usersById = new Map<string, any>(
        (Array.isArray(users) ? users : []).map((u: any) => [u.id_user, u])
      );
      const nameFor = (user: any) =>
        [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || user?.email || 'Utilisateur';

      const signups = (Array.isArray(users) ? users : [])
        .filter((u: any) => u.registration_date || u.created_at)
        .sort((a: any, b: any) =>
          new Date(b.registration_date || b.created_at).getTime() -
          new Date(a.registration_date || a.created_at).getTime()
        )
        .slice(0, 8)
        .map((user: any) => ({
          id: user.id_user,
          type: 'inscription',
          user: nameFor(user),
          action: 's\'est inscrit',
          time: user.registration_date || user.created_at,
        }));

      const rentalsRaw = rentalsResponse.ok ? await rentalsResponse.json() : [];
      const rentals = (Array.isArray(rentalsRaw) ? rentalsRaw : [])
        .filter((r: any) => r.created_at)
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 8)
        .map((rental: any) => ({
          id: rental.id_rental,
          type: 'trajet',
          user: nameFor(usersById.get(rental.renter_id)),
          action: 'a commencé un trajet',
          time: rental.created_at,
          vehicle: VEHICLE_TYPE_LABELS[rental.vehicle_type] || rental.vehicle_type || 'Véhicule',
        }));

      return { signups, rentals };
    } catch (error) {
      console.error('Erreur lors de la récupération des événements récents:', error);
      return { signups: [], rentals: [] };
    }
  }

  // Récupérer la popularité par type de mobilité (voiture, scooter, vélo...)
  async getPopularVehicles(): Promise<any[]> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      // Récupérer tous les véhicules
      const vehiclesResponse = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Récupérer toutes les locations pour calculer la popularité
      const rentalsResponse = await fetch(`${process.env.REACT_APP_API_URL}api/rentals`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (vehiclesResponse.ok && rentalsResponse.ok) {
        const vehicles = await vehiclesResponse.json();
        const rentals = await rentalsResponse.json();

        // Un type par entrée — pas un véhicule par entrée. Les locations
        // portent déjà leur `vehicle_type` (pas besoin de les rattacher à un
        // véhicule qui a pu être supprimé depuis).
        const byType = new Map<string, { vehicleCount: number; rentalCount: number }>();

        vehicles.forEach((vehicle: any) => {
          const type = vehicle.vehicle_type || 'autre';
          const entry = byType.get(type) || { vehicleCount: 0, rentalCount: 0 };
          entry.vehicleCount += 1;
          byType.set(type, entry);
        });

        rentals.forEach((rental: any) => {
          const type = rental.vehicle_type || 'autre';
          const entry = byType.get(type) || { vehicleCount: 0, rentalCount: 0 };
          entry.rentalCount += 1;
          byType.set(type, entry);
        });

        const typeStats = Array.from(byType.entries()).map(([type, entry]) => ({
          id: type,
          type,
          name: VEHICLE_TYPE_LABELS[type] || type,
          rentalCount: entry.rentalCount,
          vehicleCount: entry.vehicleCount,
          percentage: 0 // Sera calculé après
        }));

        // Trier par nombre de locations (popularité)
        const sortedTypes = typeStats.sort((a, b) => b.rentalCount - a.rentalCount);

        // Calculer les pourcentages
        const maxRentals = sortedTypes[0]?.rentalCount || 1;
        sortedTypes.forEach((entry) => {
          entry.percentage = Math.round((entry.rentalCount / maxRentals) * 100);
        });

        return sortedTypes;
      } else {
        console.error('Erreur lors de la récupération des véhicules populaires:', vehiclesResponse.status, rentalsResponse.status);
        return [];
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des véhicules populaires:', error);
      return [];
    }
  }

  // Rechercher des utilisateurs
  async searchUsers(query: string): Promise<any[]> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const users = await response.json();
        
        // Si la requête est vide, retourner les 4 premiers utilisateurs
        if (!query.trim()) {
          return users.slice(0, 4);
        }
        
        // Filtrer les utilisateurs selon la requête
        const queryLower = query.toLowerCase();
        const filteredUsers = users.filter((user: any) => {
          const searchableText = [
            user.name || '',
            user.email || '',
            user.phone || '',
            user.id || ''
          ].join(' ').toLowerCase();
          
          return searchableText.includes(queryLower);
        });
        
        return filteredUsers;
      } else {
        console.error('Erreur lors de la recherche d\'utilisateurs:', response.status);
        return [];
      }
    } catch (error) {
      console.error('Erreur lors de la recherche d\'utilisateurs:', error);
      return [];
    }
  }
}

export const useInternalViewModel = () => {
  const viewModel = InternalViewModel.getInstance();
  
  const getUsersCount = async () => {
    return await viewModel.getUsersCount();
  };

  const getVehiclesCount = async () => {
    return await viewModel.getVehiclesCount();
  };

  const getRentalsCount = async () => {
    return await viewModel.getRentalsCount();
  };

  const getDashboardStats = async () => {
    return await viewModel.getDashboardStats();
  };

  const getChartData = async (period: 'week' | 'month' | 'year' = 'month') => {
    return await viewModel.getChartData(period);
  };

  const getActiveRentals = async () => {
    return await viewModel.getActiveRentals();
  };

  const getAvailableVehicles = async () => {
    return await viewModel.getAvailableVehicles();
  };

  const getRecentEvents = async () => {
    return await viewModel.getRecentEvents();
  };

  const getPopularVehicles = async () => {
    return await viewModel.getPopularVehicles();
  };

  const searchUsers = async (query: string) => {
    return await viewModel.searchUsers(query);
  };

  return {
    getUsersCount,
    getVehiclesCount,
    getRentalsCount,
    getDashboardStats,
    getChartData,
    getActiveRentals,
    getAvailableVehicles,
    getRecentEvents,
    getPopularVehicles,
    searchUsers,
  };
}; 